import { useEffect, useRef, useState } from 'react';
import { StaticImageData } from 'next/image';
import { forwardRefWithCitrusTheme } from '@gousto-internal/citrus-react';
import { usePrefersReducedMotion } from '../../hooks/usePrefersReducedMotion';
import { MediaPlayPauseButton } from '../HomeRedesign/MediaPlayPauseButton/MediaPlayPauseButton';

const GifImage = forwardRefWithCitrusTheme('img', () => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
}));

// display:none would zero out clientWidth/clientHeight, and freezeCurrentFrame() runs
// synchronously before the isPlaying state (and its style) updates — so the canvas must
// stay laid out at all times, or the frame drawn at pause-time has nothing to size against.
const FreezeFrame = forwardRefWithCitrusTheme('canvas', () => ({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  pointerEvents: 'none',
}));

// GIFs have no play/pause API, so "pausing" draws the frame that's on screen at that
// instant onto a canvas and shows it in place of the (still-animating) <img> underneath;
// "playing" just hides the canvas again, revealing whatever frame the gif is now on.
function drawCurrentFrame(img: HTMLImageElement, canvas: HTMLCanvasElement): void {
  const ctx = canvas.getContext('2d');
  const { clientWidth: cw, clientHeight: ch } = canvas;
  const { naturalWidth: iw, naturalHeight: ih } = img;

  if (!ctx || !cw || !ch || !iw || !ih) {
    return;
  }

  canvas.width = cw;
  canvas.height = ch;

  const scale = Math.max(cw / iw, ch / ih);
  const sw = cw / scale;
  const sh = ch / scale;
  ctx.drawImage(img, (iw - sw) / 2, (ih - sh) / 2, sw, sh, 0, 0, cw, ch);
}

type PausableGifProps = {
  readonly image: StaticImageData;
  readonly alt: string;
};

export function PausableGif({ image, alt }: PausableGifProps): JSX.Element {
  const prefersReducedMotion = usePrefersReducedMotion();
  const imgRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // usePrefersReducedMotion reads window.matchMedia, which isn't available during SSR, so
  // it can only report the real preference after the client mounts. Seeding isPlaying from
  // it here would make the client's first render diverge from the server-rendered markup
  // and fail hydration; start playing (matching the server) and let the effect below
  // correct it once mounted.
  const [isPlaying, setIsPlaying] = useState(true);

  const freezeCurrentFrame = (): void => {
    if (imgRef.current && canvasRef.current) {
      drawCurrentFrame(imgRef.current, canvasRef.current);
    }
  };

  useEffect(() => {
    if (prefersReducedMotion) {
      freezeCurrentFrame();
      setIsPlaying(false);
    }
    // freezeCurrentFrame reads refs directly and isn't part of this effect's reactive inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefersReducedMotion]);

  const handleToggle = (): void => {
    if (isPlaying) {
      freezeCurrentFrame();
    }
    setIsPlaying(!isPlaying);
  };

  return (
    <>
      <GifImage ref={imgRef} src={image.src} alt={alt} onLoad={freezeCurrentFrame} />
      <FreezeFrame ref={canvasRef} aria-hidden="true" style={{ opacity: isPlaying ? 0 : 1 }} />
      <MediaPlayPauseButton isPlaying={isPlaying} onToggle={handleToggle} />
    </>
  );
}
